// src/menuApi.ts
import { getAxios } from "@coremind/identity-core";
var api = () => getAxios();
var transformMenuItem = (item) => ({
  id: item.menu_item_id,
  tenantId: item.tenant_id,
  parentId: item.parent_id,
  label: item.label,
  routePath: item.route_path ?? void 0,
  componentKey: item.component_key ?? void 0,
  iconName: item.icon_name ?? void 0,
  sortOrder: item.sort_order,
  groups: item.groups ?? [],
  permissionDescription: item.permission_description ?? void 0,
  sourceMenuItemId: item.source_menu_item_id ?? void 0,
  shellHeader: item.shell_header ?? void 0,
  menuSection: item.menu_section ?? void 0,
  isUserMenuItem: item.is_user_menu_item ?? false,
  isEnabled: item.is_enabled ?? true,
  children: (item.children ?? []).map(transformMenuItem)
});
var transformUserMenuItem = (item) => ({
  id: item.user_menu_item_id,
  tenantId: item.tenant_id,
  userId: item.user_id,
  parentId: item.parent_id,
  sourceMenuItemId: item.source_menu_item_id ?? void 0,
  label: item.label,
  routePath: item.route_path ?? void 0,
  componentKey: item.component_key ?? void 0,
  iconName: item.icon_name ?? void 0,
  shellHeader: item.shell_header ?? void 0,
  menuSection: item.menu_section ?? void 0,
  sortOrder: item.sort_order,
  isEnabled: item.is_enabled,
  children: (item.children ?? []).map(transformUserMenuItem)
});
var fetchMenu = async () => {
  const response = await api().get("/menu");
  return response.data.map(transformMenuItem);
};
var fetchAdminMenu = async () => {
  const response = await api().get("/admin/menu");
  return response.data.map(transformMenuItem);
};
var fetchUserMenu = async () => {
  const response = await api().get("/menu/user");
  return response.data.map(transformUserMenuItem);
};
var createMenuItem = async (payload) => {
  const response = await api().post(
    "/admin/menu",
    {
      label: payload.label,
      route_path: payload.routePath ?? null,
      component_key: payload.componentKey ?? null,
      icon_name: payload.iconName ?? null,
      sort_order: payload.sortOrder ?? 0,
      parent_id: payload.parentId ?? null,
      groups: payload.groups ?? [],
      create_permission: payload.createPermission ?? true,
      permission_description: payload.permissionDescription ?? null
    }
  );
  return transformMenuItem(response.data);
};
var createUserMenuItem = async (payload) => {
  const response = await api().post(
    "/menu/user",
    {
      label: payload.label,
      route_path: payload.routePath ?? null,
      component_key: payload.componentKey ?? null,
      icon_name: payload.iconName ?? null,
      shell_header: payload.shellHeader ?? null,
      menu_section: payload.menuSection ?? null,
      sort_order: payload.sortOrder ?? 0,
      parent_id: payload.parentId ?? null,
      source_menu_item_id: payload.sourceMenuItemId ?? null
    }
  );
  return transformUserMenuItem(response.data);
};
var updateMenuItem = async (menuItemId, payload) => {
  const body = {};
  if (payload.label !== void 0) body.label = payload.label;
  if (payload.routePath !== void 0) body.route_path = payload.routePath ?? null;
  if (payload.componentKey !== void 0) body.component_key = payload.componentKey ?? null;
  if (payload.iconName !== void 0) body.icon_name = payload.iconName ?? null;
  if (payload.sortOrder !== void 0) body.sort_order = payload.sortOrder ?? 0;
  if (payload.parentId !== void 0) body.parent_id = payload.parentId ?? null;
  if (payload.groups !== void 0) body.groups = payload.groups;
  if (payload.permissionDescription !== void 0) {
    body.permission_description = payload.permissionDescription ?? null;
  }
  const response = await api().put(
    `/admin/menu/${menuItemId}`,
    body
  );
  return transformMenuItem(response.data);
};
var updateUserMenuItem = async (userMenuItemId, payload) => {
  const body = {};
  if (payload.label !== void 0) body.label = payload.label;
  if (payload.routePath !== void 0) body.route_path = payload.routePath ?? null;
  if (payload.componentKey !== void 0) body.component_key = payload.componentKey ?? null;
  if (payload.iconName !== void 0) body.icon_name = payload.iconName ?? null;
  if (payload.shellHeader !== void 0) body.shell_header = payload.shellHeader ?? null;
  if (payload.menuSection !== void 0) body.menu_section = payload.menuSection ?? null;
  if (payload.sortOrder !== void 0) body.sort_order = payload.sortOrder ?? 0;
  if (payload.parentId !== void 0) body.parent_id = payload.parentId ?? null;
  if (payload.sourceMenuItemId !== void 0) {
    body.source_menu_item_id = payload.sourceMenuItemId ?? null;
  }
  if (payload.isEnabled !== void 0) body.is_enabled = payload.isEnabled;
  const response = await api().put(
    `/menu/user/${userMenuItemId}`,
    body
  );
  return transformUserMenuItem(response.data);
};
var deleteMenuItem = async (menuItemId) => {
  await api().delete(`/admin/menu/${menuItemId}`);
};
var deleteUserMenuItem = async (userMenuItemId) => {
  await api().delete(`/menu/user/${userMenuItemId}`);
};
var fetchAppRegistry = async () => {
  const response = await api().get("/apps/registry");
  return response.data;
};
var flattenMenu = (items) => {
  const result = [];
  items.forEach((item) => {
    result.push(item);
    if (item.children?.length) {
      result.push(...flattenMenu(item.children));
    }
  });
  return result;
};
var isRoutableMenuItem = (item) => Boolean(item.routePath && item.componentKey);

// src/permissionsApi.ts
import { getAxios as getAxios2 } from "@coremind/identity-core";
var api2 = () => getAxios2();
var listCognitoGroups = async () => {
  const { data } = await api2().get("/admin/cognito/groups");
  return data;
};
var createCognitoGroup = async (payload) => {
  const { data } = await api2().post("/admin/cognito/groups", payload);
  return data;
};
var addUserToCognitoGroup = async (groupName, username) => {
  await api2().post(`/admin/cognito/groups/${encodeURIComponent(groupName)}/users`, { username });
};
var removeUserFromCognitoGroup = async (groupName, username) => {
  await api2().delete(`/admin/cognito/groups/${encodeURIComponent(groupName)}/users/${encodeURIComponent(username)}`);
};
var listDirectoryRoles = async () => {
  const { data } = await api2().get("/admin/security/roles");
  return data;
};
var createDirectoryRole = async (payload) => {
  const { data } = await api2().post("/admin/security/roles", payload);
  return data;
};
var assignUserToRole = async (roleId, userId) => {
  await api2().post(`/admin/security/roles/${encodeURIComponent(roleId)}/users`, { user_id: userId });
};
var removeUserFromRole = async (roleId, userId) => {
  await api2().delete(`/admin/security/roles/${encodeURIComponent(roleId)}/users/${encodeURIComponent(userId)}`);
};
var addPermissionToRole = async (roleId, permission) => {
  await api2().post(`/admin/security/roles/${encodeURIComponent(roleId)}/permissions`, { permission });
};
var removePermissionFromRole = async (roleId, permission) => {
  await api2().delete(
    `/admin/security/roles/${encodeURIComponent(roleId)}/permissions/${encodeURIComponent(permission)}`
  );
};
var listDirectoryGroups = async () => {
  const { data } = await api2().get("/admin/security/groups");
  return data;
};
var createDirectoryGroup = async (payload) => {
  const { data } = await api2().post("/admin/security/groups", payload);
  return data;
};
var assignUserToGroup = async (groupId, userId) => {
  await api2().post(`/admin/security/groups/${encodeURIComponent(groupId)}/users`, { user_id: userId });
};
var removeUserFromGroup = async (groupId, userId) => {
  await api2().delete(
    `/admin/security/groups/${encodeURIComponent(groupId)}/users/${encodeURIComponent(userId)}`
  );
};
var addPermissionToGroup = async (groupId, permission) => {
  await api2().post(`/admin/security/groups/${encodeURIComponent(groupId)}/permissions`, { permission });
};
var removePermissionFromGroup = async (groupId, permission) => {
  await api2().delete(
    `/admin/security/groups/${encodeURIComponent(groupId)}/permissions/${encodeURIComponent(permission)}`
  );
};

// src/platformBundlesApi.ts
import { getAxios as getAxios3 } from "@coremind/identity-core";
var api3 = () => getAxios3();
var platformSiteRequestConfig = (options) => ({
  ...options?.apiBaseUrl ? { baseURL: options.apiBaseUrl } : {},
  ...options?.siteHostname ? { headers: { "X-Coremind-Site-Host": options.siteHostname } } : {}
});
var getSiteRuntime = async (siteId) => {
  const { data } = await api3().get(
    `/runtime/site/${encodeURIComponent(siteId)}`
  );
  return data;
};
var listGatekeeperSkills = async (params, options) => {
  const { data } = await api3().get(
    "/gatekeeper/skills",
    {
      ...platformSiteRequestConfig(options),
      params: { mode: params?.mode }
    }
  );
  return data;
};
var listPlatformApps = async (params) => {
  const { data } = await api3().get("/platform/apps", {
    params: {
      active_only: params?.activeOnly ?? true,
      component_key: params?.componentKey
    }
  });
  return data;
};
var updatePlatformApp = async (appId, payload) => {
  const { data } = await api3().patch(
    `/platform/apps/${encodeURIComponent(appId)}`,
    payload
  );
  return data;
};
var scaffoldPlatformApp = async (payload) => {
  const { data } = await api3().post(
    "/platform/apps/scaffold",
    payload
  );
  return data;
};
var listCorePackages = async () => {
  const { data } = await api3().get("/platform/apps/core-packages");
  return data;
};
var getPlatformAppLinkedDependencies = async (componentKey) => {
  const { data } = await api3().get(
    `/platform/apps/${encodeURIComponent(componentKey)}/linked-dependencies`
  );
  return data;
};
var updatePlatformAppLinkedDependencies = async (componentKey, payload) => {
  const { data } = await api3().put(
    `/platform/apps/${encodeURIComponent(componentKey)}/linked-dependencies`,
    payload
  );
  return data;
};
var filenameFromContentDisposition = (header) => {
  if (typeof header !== "string") return null;
  const quoted = header.match(/filename="([^"]+)"/i);
  if (quoted?.[1]) return quoted[1];
  const unquoted = header.match(/filename=([^;]+)/i);
  return unquoted?.[1]?.trim() || null;
};
var downloadPlatformAppScaffoldZip = async (payload) => {
  const response = await api3().post("/platform/apps/scaffold.zip", payload, {
    responseType: "blob"
  });
  const resultHeader = response.headers["x-coremind-scaffold-result"];
  let result = null;
  if (typeof resultHeader === "string" && resultHeader) {
    result = JSON.parse(decodeURIComponent(resultHeader));
  }
  return {
    blob: response.data,
    filename: filenameFromContentDisposition(response.headers["content-disposition"]) ?? `${payload.component_key}-federated-app.zip`,
    result
  };
};
var listDeploymentLocations = async (params) => {
  const { data } = await api3().get("/platform/locations", {
    params: {
      status: params?.status
    }
  });
  return data;
};
var createDeploymentLocation = async (payload) => {
  const { data } = await api3().post("/platform/locations", payload);
  return data;
};
var updateDeploymentLocation = async (locationId, payload) => {
  const { data } = await api3().patch(
    `/platform/locations/${encodeURIComponent(locationId)}`,
    payload
  );
  return data;
};
var listDeploymentEnvironments = async (params) => {
  const { data } = await api3().get("/platform/environments", {
    params: {
      status: params?.status
    }
  });
  return data;
};
var bootstrapDeploymentEnvironments = async () => {
  const { data } = await api3().post("/platform/environments/bootstrap");
  return data;
};
var createDeploymentEnvironment = async (payload) => {
  const { data } = await api3().post("/platform/environments", payload);
  return data;
};
var updateDeploymentEnvironment = async (environmentId, payload) => {
  const { data } = await api3().patch(
    `/platform/environments/${encodeURIComponent(environmentId)}`,
    payload
  );
  return data;
};
var observeDeploymentEnvironment = async (environmentId, payload) => {
  const { data } = await api3().post(
    `/platform/environments/${encodeURIComponent(environmentId)}/observe`,
    payload
  );
  return data;
};
var observeDeploymentEnvironmentRuntime = async (environmentId) => {
  const { data } = await api3().post(
    `/platform/environments/${encodeURIComponent(environmentId)}/observe-runtime`
  );
  return data;
};
var checkDeploymentEnvironmentDrift = async (environmentId) => {
  const { data } = await api3().post(
    `/platform/environments/${encodeURIComponent(environmentId)}/drift-check`
  );
  return data;
};
var listSiteReleaseSets = async (params) => {
  const { data } = await api3().get("/platform/release-sets", {
    params: {
      status: params?.status
    }
  });
  return data;
};
var bootstrapSiteReleaseSets = async () => {
  const { data } = await api3().post("/platform/release-sets/bootstrap");
  return data;
};
var createSiteReleaseSet = async (payload) => {
  const { data } = await api3().post("/platform/release-sets", payload);
  return data;
};
var updateSiteReleaseSet = async (releaseSetId, payload) => {
  const { data } = await api3().patch(
    `/platform/release-sets/${encodeURIComponent(releaseSetId)}`,
    payload
  );
  return data;
};
var checkPlatformSiteSlug = async (slug) => {
  const { data } = await api3().get("/platform/sites/check-slug", {
    params: { slug }
  });
  return data;
};
var listSelectableShells = async () => {
  const { data } = await api3().get("/platform/shells", {
    params: { selectable: true }
  });
  return data;
};
var listPlatformSiteTemplates = async () => {
  const { data } = await api3().get("/platform/site-templates");
  return data;
};
var listPlatformCommonModules = async () => {
  const { data } = await api3().get("/platform/common-modules");
  return data;
};
var listPlatformSites = async (params) => {
  const { data } = await api3().get("/platform/sites", {
    params: {
      environment: params?.environment
    }
  });
  return data;
};
var bootstrapPlatformSites = async (options) => {
  const { data } = await api3().post(
    "/platform/sites/bootstrap",
    null,
    platformSiteRequestConfig(options)
  );
  return data;
};
var createPlatformSiteFamily = async (payload, options) => {
  const { data } = await api3().post(
    "/platform/sites",
    payload,
    platformSiteRequestConfig(options)
  );
  return data;
};
var previewPlatformSiteFamily = async (payload) => {
  const { data } = await api3().post("/platform/sites/plan", payload);
  return data;
};
var downloadPlatformSiteArtifacts = async (payload) => {
  const { data } = await api3().post("/platform/sites/artifacts.zip", payload, {
    responseType: "blob"
  });
  return data;
};
var applyPlatformSiteArtifacts = async (payload) => {
  const { data } = await api3().post(
    "/platform/sites/artifacts/apply",
    payload
  );
  return data;
};
var createPlatformSiteEnvironment = async (slug, payload, options) => {
  const { data } = await api3().post(
    `/platform/sites/${encodeURIComponent(slug)}/environments`,
    payload,
    platformSiteRequestConfig(options)
  );
  return data;
};
var listPlatformSiteMemberships = async (slug) => {
  const { data } = await api3().get(
    `/platform/sites/${encodeURIComponent(slug)}/memberships`
  );
  return data;
};
var createPlatformSiteMembership = async (slug, payload) => {
  const { data } = await api3().post(
    `/platform/sites/${encodeURIComponent(slug)}/memberships`,
    payload
  );
  return data;
};
var updatePlatformSiteMembership = async (slug, membershipId, payload) => {
  const { data } = await api3().patch(
    `/platform/sites/${encodeURIComponent(slug)}/memberships/${encodeURIComponent(membershipId)}`,
    payload
  );
  return data;
};
var deletePlatformSiteMembership = async (slug, membershipId) => {
  await api3().delete(
    `/platform/sites/${encodeURIComponent(slug)}/memberships/${encodeURIComponent(membershipId)}`
  );
};
var deletePlatformSite = async (siteId) => {
  await api3().delete(`/platform/sites/${encodeURIComponent(siteId)}`);
};
var deletePlatformSiteByHostname = async (hostname, options) => {
  await api3().delete(
    `/platform/sites/by-hostname/${encodeURIComponent(hostname)}`,
    platformSiteRequestConfig({ ...options, siteHostname: options?.siteHostname ?? hostname })
  );
};
var updatePlatformSiteSettings = async (siteId, payload) => {
  const { data } = await api3().patch(
    `/platform/sites/${encodeURIComponent(siteId)}/settings`,
    payload
  );
  return data;
};
var updatePlatformSiteReleaseSet = async (siteId, releaseSetId) => {
  const { data } = await api3().patch(
    `/platform/sites/${encodeURIComponent(siteId)}/release-set`,
    { release_set_id: releaseSetId }
  );
  return data;
};
var listPlatformSiteBackups = async (slug) => {
  const { data } = await api3().get(
    `/platform/sites/${encodeURIComponent(slug)}/backups`
  );
  return data;
};
var backupPlatformSiteFamily = async (slug) => {
  const { data } = await api3().post(
    `/platform/sites/${encodeURIComponent(slug)}/backups`
  );
  return data;
};
var restorePlatformSiteFamily = async (slug, key) => {
  const { data } = await api3().post(
    `/platform/sites/${encodeURIComponent(slug)}/restore`,
    { key }
  );
  return data;
};
var getPlatformSiteFamilyConfig = async (slug) => {
  const { data } = await api3().get(`/platform/sites/${encodeURIComponent(slug)}/config`);
  return data;
};
var applyPlatformSiteFamilyConfig = async (slug, payload) => {
  const { data } = await api3().put(`/platform/sites/${encodeURIComponent(slug)}/config`, payload);
  return data;
};
var listPlatformSiteApps = async (siteId, options) => {
  const { data } = await api3().get(
    `/platform/sites/${encodeURIComponent(siteId)}/apps`,
    platformSiteRequestConfig(options)
  );
  return data;
};
var listPlatformSiteAppsByHostname = async (hostname, options) => {
  const { data } = await api3().get(
    `/platform/sites/by-hostname/${encodeURIComponent(hostname)}/apps`,
    platformSiteRequestConfig({ ...options, siteHostname: options?.siteHostname ?? hostname })
  );
  return data;
};
var listPlatformSiteWorkspaces = async (siteId, options) => {
  const { data } = await api3().get(
    `/platform/sites/${encodeURIComponent(siteId)}/workspaces`,
    platformSiteRequestConfig(options)
  );
  return data;
};
var listManagedSiteWorkspaces = async (hostname, options) => {
  const params = new URLSearchParams();
  if (options?.apiBaseUrl) params.set("remote_api_base_url", options.apiBaseUrl);
  const query = params.toString();
  const { data } = await api3().get(
    `/platform/master-catalog/sites/by-hostname/${encodeURIComponent(hostname)}/workspaces${query ? `?${query}` : ""}`,
    platformSiteRequestConfig({ siteHostname: options?.siteHostname ?? hostname })
  );
  return data;
};
var updatePlatformSiteApp = async (siteId, componentKey, payload, options) => {
  const { data } = await api3().put(
    `/platform/sites/${encodeURIComponent(siteId)}/apps/${encodeURIComponent(componentKey)}`,
    payload,
    platformSiteRequestConfig(options)
  );
  return data;
};
var updatePlatformSiteAppByHostname = async (hostname, componentKey, payload, options) => {
  const { data } = await api3().put(
    `/platform/sites/by-hostname/${encodeURIComponent(hostname)}/apps/${encodeURIComponent(componentKey)}`,
    payload,
    platformSiteRequestConfig({ ...options, siteHostname: options?.siteHostname ?? hostname })
  );
  return data;
};
var validatePlatformSite = async (siteId) => {
  const { data } = await api3().get(
    `/platform/sites/${encodeURIComponent(siteId)}/validation`
  );
  return data;
};
var validatePlatformSiteByHostname = async (hostname) => {
  const { data } = await api3().get(
    `/platform/sites/by-hostname/${encodeURIComponent(hostname)}/validation`
  );
  return data;
};
var getSiteRuntimeStatus = async (slug, environment) => {
  const { data } = await api3().get(
    `/platform/sites/${encodeURIComponent(slug)}/runtime`,
    { params: { environment } }
  );
  return data;
};
var applySiteRuntimeAction = async (slug, environment, action) => {
  const { data } = await api3().post(
    `/platform/sites/${encodeURIComponent(slug)}/runtime/actions`,
    { action },
    { params: { environment } }
  );
  return data;
};
var promotePlatformApp = async (payload) => {
  const { data } = await api3().post("/platform/promotions", payload);
  return data;
};
var promotePlatformReleaseSet = async (payload) => {
  const { data } = await api3().post("/platform/release-set-promotions", payload);
  return data;
};
var listPlatformPromotions = async (params) => {
  const { data } = await api3().get("/platform/promotions", {
    params: {
      component_key: params?.component_key || void 0,
      environment_key: params?.environment_key || void 0,
      limit: params?.limit
    }
  });
  return data;
};
var syncMasterAppCatalog = async (payload) => {
  const { data } = await api3().post(
    "/platform/master-catalog/sync",
    payload ?? {}
  );
  return data;
};
var listMasterCatalogApps = async () => {
  const { data } = await api3().get("/platform/master-catalog");
  return data;
};
var listMasterCatalogAppDeployments = async (componentKey) => {
  const { data } = await api3().get(
    `/platform/master-catalog/apps/${encodeURIComponent(componentKey)}/deployments`
  );
  return data;
};
var publishMasterCatalogAppDevDeployment = async (componentKey, payload) => {
  const { data } = await api3().post(
    `/platform/master-catalog/apps/${encodeURIComponent(componentKey)}/deployments/dev-publish`,
    payload ?? {}
  );
  return data;
};
var approveMasterCatalogAppDeployment = async (componentKey, version, payload) => {
  const { data } = await api3().post(
    `/platform/master-catalog/apps/${encodeURIComponent(componentKey)}/deployments/${encodeURIComponent(version)}/approve`,
    payload ?? {}
  );
  return data;
};
var updateMasterCatalogAppDefaultVersion = async (componentKey, defaultReleaseVersion) => {
  const { data } = await api3().put(
    `/platform/master-catalog/apps/${encodeURIComponent(componentKey)}/default-version`,
    { default_release_version: defaultReleaseVersion }
  );
  return data;
};
var listMasterCatalogAppPolicies = async (componentKey) => {
  const { data } = await api3().get(
    `/platform/master-catalog/apps/${encodeURIComponent(componentKey)}/policies`
  );
  return data;
};
var updateMasterCatalogAppPolicy = async (componentKey, payload) => {
  const { data } = await api3().put(
    `/platform/master-catalog/apps/${encodeURIComponent(componentKey)}/policies`,
    payload
  );
  return data;
};
var deleteMasterCatalogAppPolicy = async (componentKey, siteId) => {
  const { data } = await api3().delete(
    `/platform/master-catalog/apps/${encodeURIComponent(componentKey)}/policies/${encodeURIComponent(siteId)}`
  );
  return data;
};
var listManagedSiteSyncHistory = async (params) => {
  const { data } = await api3().get("/platform/master-catalog/history", {
    params: {
      hostname: params?.hostname || void 0,
      limit: params?.limit
    }
  });
  return data;
};
var checkManagedSiteDrift = async (hostname, payload) => {
  const { data } = await api3().post(
    `/platform/master-catalog/sites/by-hostname/${encodeURIComponent(hostname)}/drift-check`,
    payload ?? {}
  );
  return data;
};
var syncManagedSiteFromManagement = async (hostname, payload) => {
  const { data } = await api3().post(
    `/platform/master-catalog/sites/by-hostname/${encodeURIComponent(hostname)}/sync-remote`,
    payload ?? {}
  );
  return data;
};
var syncManagedSiteByHostname = async (hostname, payload, options) => {
  const { data } = await api3().post(
    `/platform/sites/by-hostname/${encodeURIComponent(hostname)}/managed-sync`,
    payload ?? {},
    platformSiteRequestConfig({ ...options, siteHostname: options?.siteHostname ?? hostname })
  );
  return data;
};
var listAppDependencies = async (appId) => {
  const { data } = await api3().get(
    `/platform/apps/${encodeURIComponent(appId)}/dependencies`
  );
  return data;
};
var replaceAppDependencies = async (appId, payload) => {
  const { data } = await api3().put(
    `/platform/apps/${encodeURIComponent(appId)}/dependencies`,
    payload
  );
  return data;
};
var listPlatformBundles = async (params) => {
  const { data } = await api3().get("/platform/bundles", {
    params: {
      active_only: params?.activeOnly ?? true,
      industry_key: params?.industryKey
    }
  });
  return data;
};
var getPlatformBundle = async (bundleId) => {
  const { data } = await api3().get(
    `/platform/bundles/${encodeURIComponent(bundleId)}`
  );
  return data;
};
var createPlatformBundle = async (payload) => {
  const { data } = await api3().post("/platform/bundles", payload);
  return data;
};
var updatePlatformBundle = async (bundleId, payload) => {
  const { data } = await api3().patch(
    `/platform/bundles/${encodeURIComponent(bundleId)}`,
    payload
  );
  return data;
};
var deletePlatformBundle = async (bundleId) => {
  await api3().delete(`/platform/bundles/${encodeURIComponent(bundleId)}`);
};
var syncStarterBundles = async (params) => {
  const { data } = await api3().post(
    "/platform/bundles/starter-sync",
    null,
    {
      params: {
        strict: params?.strict ?? false
      }
    }
  );
  return data;
};
var syncAppDependencies = async (params) => {
  const { data } = await api3().post(
    "/platform/apps/dependency-sync",
    null,
    {
      params: {
        strict: params?.strict ?? false
      }
    }
  );
  return data;
};
var syncCatalogApps = async (options) => {
  const { data } = await api3().post(
    "/platform/apps/catalog-sync",
    null,
    platformSiteRequestConfig(options)
  );
  return data;
};
var listFederatedAppDiscovery = async () => {
  const { data } = await api3().get(
    "/platform/federated-app-discovery"
  );
  return data;
};
var promoteFederatedApps = async (payload) => {
  const { data } = await api3().post(
    "/platform/federated-app-discovery/promote",
    payload
  );
  return data;
};
var listBundleMembers = async (bundleId) => {
  const { data } = await api3().get(
    `/platform/bundles/${encodeURIComponent(bundleId)}/apps`
  );
  return data;
};
var replaceBundleMembers = async (bundleId, payload) => {
  const { data } = await api3().put(
    `/platform/bundles/${encodeURIComponent(bundleId)}/apps`,
    payload
  );
  return data;
};
var listShellProfiles = async (params) => {
  const { data } = await api3().get("/platform/shell-profiles", {
    params: { active_only: params?.activeOnly ?? true }
  });
  return data;
};
var getShellProfile = async (shellProfileId) => {
  const { data } = await api3().get(
    `/platform/shell-profiles/${encodeURIComponent(shellProfileId)}`
  );
  return data;
};
var createShellProfile = async (payload) => {
  const { data } = await api3().post("/platform/shell-profiles", payload);
  return data;
};
var updateShellProfile = async (shellProfileId, payload) => {
  const { data } = await api3().patch(
    `/platform/shell-profiles/${encodeURIComponent(shellProfileId)}`,
    payload
  );
  return data;
};
var deleteShellProfile = async (shellProfileId) => {
  await api3().delete(`/platform/shell-profiles/${encodeURIComponent(shellProfileId)}`);
};
var listTenantBundlePolicies = async (tenantId) => {
  const { data } = await api3().get(
    `/platform/tenants/${encodeURIComponent(tenantId)}/bundle-policies`
  );
  return data;
};
var replaceTenantBundlePolicies = async (tenantId, payload) => {
  const { data } = await api3().put(
    `/platform/tenants/${encodeURIComponent(tenantId)}/bundle-policies`,
    payload
  );
  return data;
};
var listInstallResolutionEvents = async (params) => {
  const { data } = await api3().get("/platform/install-resolution-events", {
    params: {
      tenant_id: params.tenantId,
      user_id: params.userId,
      status: params.status
    }
  });
  return data;
};
var listPrivateAppAuditEvents = async (params) => {
  const { data } = await api3().get("/platform/private-app-audit-events", {
    params: {
      tenant_id: params?.tenantId,
      shell_id: params?.shellId,
      app_id: params?.appId,
      user_id: params?.userId,
      event_family: params?.eventFamily,
      outcome: params?.outcome,
      limit: params?.limit
    }
  });
  return data;
};
var listPrivateAppAuditWarnings = async (params) => {
  const { data } = await api3().get("/platform/private-app-audit-warnings", {
    params: {
      tenant_id: params?.tenantId,
      shell_id: params?.shellId,
      app_id: params?.appId,
      limit: params?.limit
    }
  });
  return data;
};
var getPrivateAppAuditSummary = async (params) => {
  const { data } = await api3().get("/platform/private-app-audit-summary", {
    params: {
      tenant_id: params?.tenantId,
      shell_id: params?.shellId,
      app_id: params?.appId,
      limit: params?.limit
    }
  });
  return data;
};
var previewWorkspaceInstall = async (tenantId, payload) => {
  const { data } = await api3().post(
    `/workspaces/${encodeURIComponent(tenantId)}/install-preview`,
    payload
  );
  return data;
};
var commitWorkspaceInstall = async (tenantId, payload) => {
  const { data } = await api3().post(
    `/workspaces/${encodeURIComponent(tenantId)}/installs`,
    payload
  );
  return data;
};
var uninstallWorkspaceTarget = async (tenantId, payload) => {
  const { data } = await api3().delete(
    `/workspaces/${encodeURIComponent(tenantId)}/installs`,
    { data: payload }
  );
  return data;
};
var listWorkspaceApps = async (tenantId, params) => {
  const { data } = await api3().get(
    `/workspaces/${encodeURIComponent(tenantId)}/apps`,
    {
      params: {
        include_hidden_support_apps: params?.includeHiddenSupportApps ?? false
      }
    }
  );
  return data;
};
var listWorkspaceBundles = async (tenantId) => {
  const { data } = await api3().get(
    `/workspaces/${encodeURIComponent(tenantId)}/bundles`
  );
  return data;
};
var getWorkspaceShellProfile = async (tenantId) => {
  const { data } = await api3().get(
    `/workspaces/${encodeURIComponent(tenantId)}/shell-profile`
  );
  return data;
};
var listWorkspaceInstallResolutionEvents = async (tenantId, params) => {
  const { data } = await api3().get(
    `/workspaces/${encodeURIComponent(tenantId)}/install-resolution-events`,
    {
      params: {
        user_id: params?.userId,
        status: params?.status
      }
    }
  );
  return data;
};
var previewMyInstall = async (payload) => {
  const { data } = await api3().post("/me/install-preview", payload);
  return data;
};
var listMyInstalledBundles = async () => {
  const { data } = await api3().get("/me/bundles/installed");
  return data;
};
var listMyBundleStore = async () => {
  const { data } = await api3().get("/me/bundles/store");
  return data;
};
var commitMyInstall = async (payload) => {
  const { data } = await api3().post("/me/installs", payload);
  return data;
};
var uninstallMyInstall = async (payload) => {
  const { data } = await api3().delete("/me/installs", { data: payload });
  return data;
};
export {
  addPermissionToGroup,
  addPermissionToRole,
  addUserToCognitoGroup,
  applyPlatformSiteArtifacts,
  applyPlatformSiteFamilyConfig,
  applySiteRuntimeAction,
  approveMasterCatalogAppDeployment,
  assignUserToGroup,
  assignUserToRole,
  backupPlatformSiteFamily,
  bootstrapDeploymentEnvironments,
  bootstrapPlatformSites,
  bootstrapSiteReleaseSets,
  checkDeploymentEnvironmentDrift,
  checkManagedSiteDrift,
  checkPlatformSiteSlug,
  commitMyInstall,
  commitWorkspaceInstall,
  createCognitoGroup,
  createDeploymentEnvironment,
  createDeploymentLocation,
  createDirectoryGroup,
  createDirectoryRole,
  createMenuItem,
  createPlatformBundle,
  createPlatformSiteEnvironment,
  createPlatformSiteFamily,
  createPlatformSiteMembership,
  createShellProfile,
  createSiteReleaseSet,
  createUserMenuItem,
  deleteMasterCatalogAppPolicy,
  deleteMenuItem,
  deletePlatformBundle,
  deletePlatformSite,
  deletePlatformSiteByHostname,
  deletePlatformSiteMembership,
  deleteShellProfile,
  deleteUserMenuItem,
  downloadPlatformAppScaffoldZip,
  downloadPlatformSiteArtifacts,
  fetchAdminMenu,
  fetchAppRegistry,
  fetchMenu,
  fetchUserMenu,
  flattenMenu,
  getPlatformAppLinkedDependencies,
  getPlatformBundle,
  getPlatformSiteFamilyConfig,
  getPrivateAppAuditSummary,
  getShellProfile,
  getSiteRuntime,
  getSiteRuntimeStatus,
  getWorkspaceShellProfile,
  isRoutableMenuItem,
  listAppDependencies,
  listBundleMembers,
  listCognitoGroups,
  listCorePackages,
  listDeploymentEnvironments,
  listDeploymentLocations,
  listDirectoryGroups,
  listDirectoryRoles,
  listFederatedAppDiscovery,
  listGatekeeperSkills,
  listInstallResolutionEvents,
  listManagedSiteSyncHistory,
  listManagedSiteWorkspaces,
  listMasterCatalogAppDeployments,
  listMasterCatalogAppPolicies,
  listMasterCatalogApps,
  listMyBundleStore,
  listMyInstalledBundles,
  listPlatformApps,
  listPlatformBundles,
  listPlatformCommonModules,
  listPlatformPromotions,
  listPlatformSiteApps,
  listPlatformSiteAppsByHostname,
  listPlatformSiteBackups,
  listPlatformSiteMemberships,
  listPlatformSiteTemplates,
  listPlatformSiteWorkspaces,
  listPlatformSites,
  listPrivateAppAuditEvents,
  listPrivateAppAuditWarnings,
  listSelectableShells,
  listShellProfiles,
  listSiteReleaseSets,
  listTenantBundlePolicies,
  listWorkspaceApps,
  listWorkspaceBundles,
  listWorkspaceInstallResolutionEvents,
  observeDeploymentEnvironment,
  observeDeploymentEnvironmentRuntime,
  previewMyInstall,
  previewPlatformSiteFamily,
  previewWorkspaceInstall,
  promoteFederatedApps,
  promotePlatformApp,
  promotePlatformReleaseSet,
  publishMasterCatalogAppDevDeployment,
  removePermissionFromGroup,
  removePermissionFromRole,
  removeUserFromCognitoGroup,
  removeUserFromGroup,
  removeUserFromRole,
  replaceAppDependencies,
  replaceBundleMembers,
  replaceTenantBundlePolicies,
  restorePlatformSiteFamily,
  scaffoldPlatformApp,
  syncAppDependencies,
  syncCatalogApps,
  syncManagedSiteByHostname,
  syncManagedSiteFromManagement,
  syncMasterAppCatalog,
  syncStarterBundles,
  uninstallMyInstall,
  uninstallWorkspaceTarget,
  updateDeploymentEnvironment,
  updateDeploymentLocation,
  updateMasterCatalogAppDefaultVersion,
  updateMasterCatalogAppPolicy,
  updateMenuItem,
  updatePlatformApp,
  updatePlatformAppLinkedDependencies,
  updatePlatformBundle,
  updatePlatformSiteApp,
  updatePlatformSiteAppByHostname,
  updatePlatformSiteMembership,
  updatePlatformSiteReleaseSet,
  updatePlatformSiteSettings,
  updateShellProfile,
  updateSiteReleaseSet,
  updateUserMenuItem,
  validatePlatformSite,
  validatePlatformSiteByHostname
};
